import { EditOutlined } from '@ant-design/icons'
import { getNominative } from '@lmc/common/utils'
import {
  type Carer,
  type Home,
  type HomeDto,
  ResidentStatus,
  UserRole
} from '@lmc/ts-common'
import { Button, PageHeader, Spinner, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Tooltip } from 'antd'
import { capitalize } from 'lodash'
import type { FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useRotaPatternDetail } from '../hooks/useRotaPatternDetail'
import { GeneralRotaInformation } from './components'
import type { PopulatedRotaPatternResponse } from './components/GeneralRotaInformation'
import RecurrencePattern from './components/RecurrencePattern'
import TeamMembersAndSkills from './components/TeamMembersAndSkills'
import type { ModalSectionProps, RotaPatternModalType } from './utils'

type RotaPatternViewProps = {
  type: RotaPatternModalType
} & ModalSectionProps

export const RotaPatternView: FC<RotaPatternViewProps> = ({ type }) => {
  const { t } = useTranslation()

  const { id } = useParams()

  const navigate = useNavigate()

  const home: HomeDto | undefined = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )
  const isAuditView = userRole === UserRole.auditView

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )

  const { patternDetail, isLoadingDetail } = useRotaPatternDetail({
    id
  })

  const residentStatus =
    type === 'visit' ? patternDetail?.resident?.status : undefined

  const residentId = type === 'visit' ? patternDetail?.resident?.id : undefined

  if (isLoadingDetail) {
    return (
      <LoadingWrapper>
        <Spinner />
      </LoadingWrapper>
    )
  }

  return (
    <>
      <PageHeader
        title={t('THE_PATTERN', { variable: capitalize(type) })}
        onBack={() => navigate(`/rota/patterns?tabKey=${type}s`)}
      >
        <ButtonsWrapper>
          {!isAuditView &&
            (residentStatus && residentStatus !== ResidentStatus.active ? (
              <Tooltip
                color={theme.color.neutral.color3}
                placement="bottomRight"
                title={
                  <TooltipTitle>
                    <Trans
                      i18nKey="SU_INACTIVE_MAKE_ACTIVE"
                      values={{
                        nominative: residentNominative,
                        residentStatus
                      }}
                      components={{
                        profile: (
                          <StyledLink
                            to={`${window.location.origin}/residents/${residentId}/profile`}
                            target="_blank"
                          />
                        )
                      }}
                    />
                  </TooltipTitle>
                }
              >
                <Button disabled>
                  <EditOutlined />
                  {t('EDIT')}
                </Button>
              </Tooltip>
            ) : (
              <Button
                variant="primary"
                onClick={() => navigate(`/rota/${id}/${type}/edit`)}
                type="submit"
              >
                <EditOutlined />
                {t('EDIT')}
              </Button>
            ))}
        </ButtonsWrapper>
      </PageHeader>
      <GeneralRotaInformation
        home={home as HomeDto}
        pattern={patternDetail as unknown as PopulatedRotaPatternResponse}
        type={type}
      />
      <DetailsWrapper>
        <RecurrencePattern pattern={patternDetail} />
        <TeamMembersAndSkills pattern={patternDetail} />
      </DetailsWrapper>
    </>
  )
}

const ButtonsWrapper = styled.div`
  display: flex;
  margin-left: auto;
  gap: 5px;
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`
const DetailsWrapper = styled.div`
  display: flex;
  gap: 24px;
`
const TooltipTitle = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color11};
`
const StyledLink = styled(Link)`
  text-decoration: underline;
  color: ${(props) => props.theme.color.status.primary};
`
